import React from 'react';
import styled from 'styled-components';
import { Modal, Button, Space, Popconfirm, message, Tooltip } from 'antd';
import {
  DownloadOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useAtomValue } from 'jotai';
import { selectedFreePhotoAtom, freePhotoListAtom } from '~/stores/FreePhotoStore';
import { PhotoViewer } from './PhotoViewer';
import { PhotoMetadataPanel } from './PhotoMetadataPanel';
import { useFreePhotoMetadata } from '../hooks/useFreePhotoMetadata';
import { useFreePhotoDownload } from '../hooks/useFreePhotoDownload';
import type { FreePhoto_Base } from '~/common/typescript/models/FreePhoto';

interface FreePhotoDetailModalProps {
  visible: boolean;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

export const FreePhotoDetailModal: React.FC<FreePhotoDetailModalProps> = ({ visible, onClose, onNavigate }) => {
  const selectedPhoto = useAtomValue(selectedFreePhotoAtom);
  const photos = useAtomValue(freePhotoListAtom);
  const metadata = useFreePhotoMetadata(selectedPhoto);
  const { downloadPhoto, downloading } = useFreePhotoDownload();

  // Current position in list
  const currentIndex = selectedPhoto ? photos.findIndex((p) => p.uuid === selectedPhoto.uuid) : -1;
  const total = photos.length;
  const hasPrev = currentIndex > 0 || total > 1;
  const hasNext = currentIndex < total - 1 || total > 1;

  const handleDownload = () => {
    if (selectedPhoto) downloadPhoto(selectedPhoto.uuid, selectedPhoto.file_name);
  };

  const handleShare = () => {
    if (selectedPhoto) {
      navigator.clipboard.writeText(`${window.location.origin}/free-photos/${selectedPhoto.uuid}`);
      message.success('Đã sao chép link chia sẻ');
    }
  };

  const handleDelete = () => {
    message.info('Chức năng xóa sẽ được triển khai sau');
    onClose();
  };

  if (!selectedPhoto) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
      title={
        <UI_ModalHeader>
          {/* Left: prev/next navigation */}
          <UI_NavGroup>
            {onNavigate && (
              <>
                <Tooltip title="Ảnh trước (←)">
                  <Button
                    icon={<LeftOutlined />}
                    onClick={() => onNavigate('prev')}
                    disabled={!hasPrev}
                    size="small"
                  />
                </Tooltip>
                {total > 0 && (
                  <UI_Counter>
                    {currentIndex + 1} / {total}
                  </UI_Counter>
                )}
                <Tooltip title="Ảnh tiếp (→)">
                  <Button
                    icon={<RightOutlined />}
                    onClick={() => onNavigate('next')}
                    disabled={!hasNext}
                    size="small"
                  />
                </Tooltip>
              </>
            )}
          </UI_NavGroup>

          {/* Right: actions */}
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownload} loading={downloading}>
              Tải xuống
            </Button>
            <Button icon={<ShareAltOutlined />} onClick={handleShare}>
              Chia sẻ
            </Button>
            <Popconfirm
              title="Xác nhận xóa ảnh?"
              description="Bạn có chắc chắn muốn xóa ảnh này không?"
              onConfirm={handleDelete}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<DeleteOutlined />} danger>
                Xóa
              </Button>
            </Popconfirm>
          </Space>
        </UI_ModalHeader>
      }
    >
      <UI_Content>
        <UI_ImageSection>
          <PhotoViewer uuid={selectedPhoto.uuid} alt={selectedPhoto.file_name} />
        </UI_ImageSection>
        <UI_MetadataSection>
          <PhotoMetadataPanel
            metadata={metadata}
            fileName={selectedPhoto.file_name}
            createdAt={selectedPhoto.created_at}
            projectId={selectedPhoto.project_id}
            userId={selectedPhoto.user_id}
          />
        </UI_MetadataSection>
      </UI_Content>
    </Modal>
  );
};

const UI_ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 32px; /* space for close button */
`;

const UI_NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UI_Counter = styled.span`
  font-size: 13px;
  color: #666;
  min-width: 48px;
  text-align: center;
  font-weight: normal;
`;

const UI_Content = styled.div`
  display: flex;
  height: 70vh;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const UI_ImageSection = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const UI_MetadataSection = styled.div`
  width: 320px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
`;